// src/components/UserManagement/UserManagement.jsx
import {
  ClearOutlined,
  EllipsisOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Menu,
  Modal,
  Row,
  Space,
  Table,
  Tag,
} from "antd";
import dayjs from "dayjs";
import { SearchIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  createOrUpdateAgentAndCustomer,
  filterUsers,
  suspendUser,
} from "../../api/UserAPI.js";
import { useGlobalUI } from "../../context/GlobalUIProvider.jsx";
import {
  decryptData,
  formatIdWithPrefix,
  maskString,
} from "../../Custom/Custom.js";
import FindUserModal from "./FindUserModal.jsx";
import UserDetailsCard from "./UserDetailsCard.jsx";
import UserFilterModal from "./UserFilterModal.jsx"; // ⬅ Imported filter modal
import UserFormModal from "./UserFormModal.jsx";

// Status Badge
const getStatusBadge = (status) => {
  const label = status === 1 || status === "Active" ? "Active" : "Suspended";
  const color = label === "Active" ? "green" : "red";
  return (
    <Tag color={color} style={{ borderRadius: 12, fontWeight: 500 }}>
      {label}
    </Tag>
  );
};

// KYC Badge
const getKycBadge = (kyc) => {
  const label =
    kyc?.status === "PENDING"
      ? "Pending"
      : kyc?.status === "APPROVED"
      ? "Approved"
      : kyc?.status === "REJECTED"
      ? "Rejected"
      : "Under Review";
  const color =
    label === "Approved"
      ? "green"
      : label === "Pending"
      ? "gold"
      : label === "Rejected"
      ? "red"
      : "default";
  return (
    <Tag color={color} style={{ borderRadius: 12, fontWeight: 500 }}>
      {label}
    </Tag>
  );
};

// Actions Dropdown
function ActionMenu({
  user,
  onEdit,
  onSuspend,
  onViewDetails,
  onResetPassword,
}) {
  const menu = (
    <Menu>
      <Menu.Item onClick={() => onViewDetails(user)}>View Details</Menu.Item>
      <Menu.Item onClick={() => onEdit(user)}>Edit User</Menu.Item>
      <Menu.Item onClick={() => onResetPassword(user)}>
        Reset Password
      </Menu.Item>
      <Menu.Item danger onClick={() => onSuspend(user.id)}>
        Suspend User
      </Menu.Item>
    </Menu>
  );
  return (
    <Dropdown overlay={menu} trigger={["click"]}>
      <Button
        type="text"
        icon={<EllipsisOutlined style={{ fontSize: 20 }} />}
      />
    </Dropdown>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    currentPage: 0,
    pageSize: 10,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFindUserModalOpen, setIsFindUserModalOpen] = useState(false);
  const [isMasked, setIsMasked] = useState(true);
  const [filters, setFilters] = useState({
    userType: null,
    userStatus: null,
    dob: null,
    search: "",
  });
  const { showLoader, hideLoader, message } = useGlobalUI();
  // Fetch Users
  const fetchUsers = (page = 0, size = 10, filterData = {}) => {
    showLoader();
    return filterUsers({ pageNo: page, size, ...filterData })
      .then((res) => {
        if (res.data && res.data.data) {
          const { users, totalItems, currentPage } = res.data.data;
          setUsers(users || []);

          setPagination({
            totalItems: totalItems || 0,
            currentPage: currentPage || 0,
            pageSize: size,
          });

          return (users && users.length) || 0; // ✅ return count of users
        } else {
          setUsers([]);
          return 0; // no users found
        }
      })
      .catch((err) => {
        setUsers([]);
        return Promise.reject(err); // propagate error
      })
      .finally(() => hideLoader());
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    const newFilters = { ...filters, search: value };
    setFilters(newFilters);
    fetchUsers(0, pagination.pageSize, newFilters);
  };

  const applyFilters = (payload) => {
    setFilters(payload);
    fetchUsers(0, pagination.pageSize, payload);
    setIsFilterModalOpen(false);
  };

  const handleFindUser = (filtersData) => {
    setIsFindUserModalOpen(false);

    fetchUsers(0, pagination.pageSize, filtersData)
      .then((usersCount) => {
        if (usersCount > 0) {
          message.success("✅ User fetched successfully");
        } else {
          message.warning("⚠️ User not found");
        }
      })
      .catch(() => {
        message.error("❌ Failed to fetch users");
      });

    setIsMasked(false);
  };

  const clearFilter = () => {
    setIsMasked(true);
    fetchUsers();
  };

  let columns = [
    {
      title: "User",
      dataIndex: "name",
      key: "name",
      render: (val, row) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>
            {isMasked ? maskString(val, 1, 0) : val}
          </span>
          <span style={{ color: "#888", fontSize: 12 }}>
            {formatIdWithPrefix(row?.userCode ?? row?.id, "USR")}
          </span>
        </Space>
      ),
    },
    {
      title: "Contact",
      key: "contact",
      render: (record) => (
        <>
          <div>{isMasked ? maskString(record.email, 2, 10) : record.email}</div>
          <div style={{ color: "#888" }}>
            {isMasked ? maskString(record.mobileNo, 2, 2) : record.mobileNo}
          </div>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "userStatus",
      key: "status",
      render: getStatusBadge,
    },
    {
      title: "KYC Status",
      dataIndex: "kyc",
      key: "kyc",
      render: getKycBadge,
    },
    {
      title: "Wallet Balance",
      dataIndex: ["wallet", "totalBalance"],
      key: "walletBalance",
      render: (balance) =>
        typeof balance === "number"
          ? isMasked
            ? maskString(
                `$${balance.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}`,
                0,
                0
              )
            : `$${balance.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}`
          : "$0.00",
    },
    {
      title: "Join Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (date) => date?.slice(0, 10),
    },
  ];

  // 👉 Only add Action column if NOT masked
  if (!isMasked) {
    columns.push({
      title: "",
      key: "action",
      render: (_, user) => (
        <ActionMenu
          user={user}
          onEdit={(u) => {
            setEditingUser(u);
            form.setFieldsValue({ ...u, dob: u.dob ? dayjs(u.dob) : null });
            setIsModalVisible(true);
          }}
          onSuspend={(id) => {
            suspendUser({ id })
              .then((res) => {
                if (res.data.status === 1) {
                  message.success("User suspended");
                  fetchUsers(
                    pagination.currentPage,
                    pagination.pageSize,
                    filters
                  );
                } else {
                  message.error(res.data.message || "Suspend failed");
                }
              })
              .catch(() => message.error("Failed to suspend user"));
          }}
          onViewDetails={(u) => {
            setSelectedUser(u);
            setDetailsModalVisible(true);
          }}
          onResetPassword={(u) =>
            message.info(`Resetting password for ${u.name}`)
          }
        />
      ),
    });
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        {/* Left Section */}
        <Col>
          <div style={{ fontSize: 28, fontWeight: 600 }}>User Management</div>
          <div style={{ color: "#555" }}>
            Manage user accounts, status, and permissions
          </div>
        </Col>

        {/* Right Section */}
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<ClearOutlined />}
              onClick={() => clearFilter()}
            >
              Clear
            </Button>
            <Button
              type="primary"
              icon={<SearchIcon />}
              onClick={() => setIsFindUserModalOpen(true)}
            >
              Find User
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingUser(null);
                form.resetFields();
                setIsModalVisible(true);
              }}
            >
              Add User
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Table Card */}
      <Card bodyStyle={{ padding: 0 }}>
        <div style={{ padding: "16px 24px 0 24px" }}>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Col>
              <div style={{ fontSize: 20, fontWeight: 600 }}>All Users</div>
            </Col>
          </Row>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.currentPage + 1,
            pageSize: pagination.pageSize,
            total: pagination.totalItems,
            onChange: (page) =>
              fetchUsers(page - 1, pagination.pageSize, filters),
          }}
          style={{ borderRadius: 12, margin: "12px" }}
        />
      </Card>

      {/* View Details */}
      <Modal
        title="User Details"
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={null}
        width={600}
      >
        <UserDetailsCard user={selectedUser} />
      </Modal>

      <FindUserModal
        open={isFindUserModalOpen}
        onCancel={() => setIsFindUserModalOpen(false)}
        onFind={handleFindUser}
      />

      {/* Add/Edit User */}
      <UserFormModal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onSave={(payload) => {
          setSaving(true);
          createOrUpdateAgentAndCustomer(payload)
            .then((res) => {
              if (res.data.status === 1) {
                message.success(editingUser ? "User updated" : "User added");
                fetchUsers(
                  pagination.currentPage,
                  pagination.pageSize,
                  filters
                );
                setIsModalVisible(false);
                setIsMasked(true);
              } else {
                message.error(res.data.message || "Failed to save user");
              }
            })
            .catch(() => message.error("Failed to save user"))
            .finally(() => setSaving(false));
        }}
        editingUser={editingUser}
        form={form}
        loading={saving}
      />

      {/* Filter Modal */}
      <UserFilterModal
        open={isFilterModalOpen}
        onCancel={() => setIsFilterModalOpen(false)}
        onApply={applyFilters}
      />
    </div>
  );
}
